import {useState, useCallback, useEffect, useRef} from 'react'
import {useParams, useNavigate} from 'react-router-dom'
import {useQuery, useQueryClient} from '@tanstack/react-query'
import {Button} from '@/components/ui/button'
import {Card, CardContent, CardHeader, CardTitle, CardFooter} from '@/components/ui/card'
import {Badge} from '@/components/ui/badge'
import {Input} from '@/components/ui/input'
import {Textarea} from '@/components/ui/textarea'
import {Label} from '@/components/ui/label'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {MonoText} from '@/components/shared/MonoText'
import {InlineRow} from '@/components/shared/InlineRow'
import {Stack} from '@/components/shared/Stack'
import {FormField} from '@/components/shared/FormField'
import {FormSection} from '@/components/shared/FormSection'
import {SelectableOption} from '@/components/shared/SelectableOption'
import {MutedText} from '@/components/shared/MutedText'
import {DetailsCard} from '@/components/shared/DetailsCard'
import {PageLayout} from '@/components/shared/PageLayout'
import {useToast} from '@/hooks/use-toast'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {useI18n} from '@/i18n'
import {ApiError} from '@/api/client'
import {routes} from '@/lib/routes'
import {formatTimestamp} from '@/lib/date-utils'
import {useServices} from '@/services/context'
import {CONCEPT_ICON} from '@/lib/concepts'
import type {NotificationTemplateUpdateRequest} from '@/types/notification-templates'

export function NotificationTemplateDetail() {
    const services = useServices()
    const {key} = useParams<{key: string}>()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const {toast} = useToast()
    const i18n = useI18n()

    const [subject, setSubject] = useState('')
    const [bodyHtml, setBodyHtml] = useState('')
    const [title, setTitle] = useState('')
    const [message, setMessage] = useState('')
    const [isDirty, setIsDirty] = useState(false)
    const restoreDialog = useDialog()
    const bodyRef = useRef<HTMLTextAreaElement>(null)

    const {data: template, isLoading, error} = useQuery({
        queryKey: ['notification-templates', key],
        queryFn: () => services.notificationTemplates.detail(key!),
        enabled: !!key,
    })

    useEffect(() => {
        if (template) {
            setSubject(template.subject ?? '')
            setBodyHtml(template.bodyHtml ?? '')
            setTitle(template.title ?? '')
            setMessage(template.message ?? '')
            setIsDirty(false)
        }
    }, [template])

    useEffect(() => {
        if (!isDirty) return
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault()
        }
        window.addEventListener('beforeunload', handler)
        return () => window.removeEventListener('beforeunload', handler)
    }, [isDirty])

    const updateMutation = useEntityMutation({
        mutationFn: (data: NotificationTemplateUpdateRequest) =>
            services.notificationTemplates.update(key!, data),
        invalidate: ['notification-templates'],
        successMessage: i18n.notificationTemplates.messages.saveSuccess,
        onSuccess: () => {
            setIsDirty(false)
        },
        onError: (err) => {
            if (err instanceof ApiError && err.status === 409) {
                toast({description: i18n.notificationTemplates.messages.versionConflict, variant: 'destructive'})
                queryClient.invalidateQueries({queryKey: ['notification-templates', key]})
            }
        },
    })

    const restoreMutation = useEntityMutation({
        mutationFn: () => services.notificationTemplates.restoreDefault(key!),
        invalidate: ['notification-templates'],
        successMessage: i18n.notificationTemplates.messages.restoreSuccess,
        closeDialog: restoreDialog.close,
        onSuccess: () => {
            setIsDirty(false)
        },
    })

    const testSendMutation = useEntityMutation({
        mutationFn: () => services.notificationTemplates.testSend(key!),
        invalidate: ['notifications'],
        onSuccess: (result) => {
            if (result.status === 'SENT') {
                toast({description: i18n.notificationTemplates.messages.testSendSent({email: result.recipientEmail ?? ''})})
            } else {
                toast({description: i18n.notificationTemplates.messages.testSendSkipped})
            }
        },
    })

    const markDirty = useCallback(() => setIsDirty(true), [])

    const handleSave = () => {
        if (!template) return
        updateMutation.mutate({
            subject,
            bodyHtml,
            title: title.trim() === '' ? null : title,
            message: message.trim() === '' ? null : message,
            version: template.version,
        })
    }

    const handleCancel = () => {
        navigate(routes.admin.notificationTemplates.list())
    }

    const insertVariable = (name: string) => {
        const token = `[[\${${name}}]]`
        const textarea = bodyRef.current
        if (!textarea) {
            setBodyHtml((cur) => cur + token)
            markDirty()
            return
        }
        const start = textarea.selectionStart
        const end = textarea.selectionEnd
        const next = bodyHtml.slice(0, start) + token + bodyHtml.slice(end)
        setBodyHtml(next)
        markDirty()
        requestAnimationFrame(() => {
            textarea.focus()
            const caret = start + token.length
            textarea.setSelectionRange(caret, caret)
        })
    }

    return (
        <PageLayout
            variant="detail"
            title={template?.templateKey ?? i18n.notificationTemplates.detail.title}
            icon={CONCEPT_ICON.notificationTemplate}
            loading={isLoading || !template}
            error={error}
            backTo={routes.admin.notificationTemplates.list()}
            data-testid="notification-template-detail-page"
            actions={template ? (
                template.customised ? (
                    <Badge variant="secondary" data-testid="badge-customised">
                        {i18n.notificationTemplates.badges.customised}
                    </Badge>
                ) : (
                    <Badge variant="outline" data-testid="badge-default">
                        {i18n.notificationTemplates.badges.default}
                    </Badge>
                )
            ) : undefined}
        >
            {template && <>
                <Stack gap="lg">
                    <DetailsCard
                        data-testid="card-notification-template-metadata"
                        title={i18n.notificationTemplates.detail.metadata}
                        fields={[
                            {label: i18n.notificationTemplates.columns.key, value: <MonoText>{template.templateKey}</MonoText>},
                            {label: i18n.notificationTemplates.columns.lastModified, value: formatTimestamp(template.updatedAt)},
                        ]}
                    />

                    <Card data-help-prefix="notificationTemplateDetail">
                        <CardHeader>
                            <CardTitle data-testid="card-title-editor" data-help-key="_section">{i18n.notificationTemplates.detail.editor}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <Stack gap="md">
                                    <FormField label={i18n.notificationTemplates.fields.subject} htmlFor="nt-subject" required>
                                        <Input
                                            id="nt-subject"
                                            value={subject}
                                            onChange={(e) => {setSubject(e.target.value); markDirty()}}
                                            data-testid="input-subject"
                                        />
                                    </FormField>
                                    <FormField label={i18n.notificationTemplates.fields.bodyHtml} htmlFor="nt-body" required>
                                        <Textarea
                                            id="nt-body"
                                            ref={bodyRef}
                                            value={bodyHtml}
                                            onChange={(e) => {setBodyHtml(e.target.value); markDirty()}}
                                            className="font-mono min-h-[300px]"
                                            data-testid="textarea-body"
                                        />
                                    </FormField>
                                    <FormField label={i18n.notificationTemplates.fields.title} htmlFor="nt-title">
                                        <Input
                                            id="nt-title"
                                            value={title}
                                            onChange={(e) => {setTitle(e.target.value); markDirty()}}
                                            data-testid="input-title"
                                        />
                                    </FormField>
                                    <FormField label={i18n.notificationTemplates.fields.message} htmlFor="nt-message">
                                        <Textarea
                                            id="nt-message"
                                            value={message}
                                            onChange={(e) => {setMessage(e.target.value); markDirty()}}
                                            className="resize-y"
                                            data-testid="textarea-message"
                                        />
                                    </FormField>
                                    <FormSection
                                        title={i18n.notificationTemplates.detail.variables}
                                        data-testid="variable-picker"
                                    >
                                        {template.variables.length === 0 ? (
                                            <MutedText size="sm">{i18n.notificationTemplates.detail.noVariables}</MutedText>
                                        ) : (
                                            <Stack gap="sm">
                                                <MutedText size="sm">{i18n.notificationTemplates.detail.variableHint}</MutedText>
                                                {template.variables.map((v) => (
                                                    <SelectableOption
                                                        key={v.name}
                                                        selected={false}
                                                        onClick={() => insertVariable(v.name)}
                                                        data-testid={`variable-${v.name}`}
                                                    >
                                                        <Stack gap="xs">
                                                            <Label className="font-mono">{`[[\${${v.name}}]]`}</Label>
                                                            <MutedText size="sm">{v.description}</MutedText>
                                                        </Stack>
                                                    </SelectableOption>
                                                ))}
                                            </Stack>
                                        )}
                                    </FormSection>
                            </Stack>
                        </CardContent>
                        <CardFooter className="flex justify-between">
                            <InlineRow>
                                <Button
                                    type="button"
                                    variant="destructive"
                                    onClick={restoreDialog.open}
                                    disabled={!template.customised}
                                    data-testid="btn-restore-default"
                                >
                                    {i18n.notificationTemplates.actions.restoreDefault}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => testSendMutation.mutate()}
                                    disabled={testSendMutation.isPending || isDirty}
                                    title={isDirty ? i18n.notificationTemplates.messages.testSendDirtyHint : undefined}
                                    data-testid="btn-test-send"
                                >
                                    {i18n.notificationTemplates.actions.testSend}
                                </Button>
                            </InlineRow>
                            <InlineRow>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleCancel}
                                    data-testid="btn-cancel"
                                >
                                    {i18n.common.cancel}
                                </Button>
                                <Button
                                    type="button"
                                    onClick={handleSave}
                                    disabled={!isDirty || updateMutation.isPending}
                                    data-testid="btn-save"
                                >
                                    {updateMutation.isPending ? i18n.common.saving : i18n.common.save}
                                </Button>
                            </InlineRow>
                        </CardFooter>
                    </Card>
                </Stack>

                <Dialog open={restoreDialog.isOpen} onOpenChange={restoreDialog.bind.onOpenChange}>
                    <DialogContent data-testid="dialog-restore-default">
                        <DialogHeader>
                            <DialogTitle>{i18n.notificationTemplates.messages.restoreConfirm}</DialogTitle>
                            <DialogDescription>
                                {i18n.notificationTemplates.messages.restoreConfirmDescription}
                            </DialogDescription>
                        </DialogHeader>
                        <SaveCancelFooter
                            testIdPrefix="restore"
                            onCancel={restoreDialog.close}
                            saving={restoreMutation.isPending}
                            saveLabel={i18n.notificationTemplates.actions.restoreDefault}
                            destructive
                            saveType="button"
                            onSave={() => restoreMutation.mutate()}
                        />
                    </DialogContent>
                </Dialog>
            </>}
        </PageLayout>
    )
}
